import { useLocale } from "../../../hooks/useLocale";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import styled from "styled-components";
import { FormParser } from "src/view/FormParser";
import { GD } from "src/GD";
import { AdminButton, AdminInput } from "./AdminControls";
import { AssignmentOverlay, AssignmentSurface } from "./TrafficAssignments";
import { TrafficRuleSummary } from "./TrafficRuleSummary";
import { saveAssignmentForm } from "./assignmentSave";
import { banRuleId, getBanRulesRequest, parseBanRules } from "./banRulesData";
import { readRulePreview } from "./rulePreviewData";
import { ruleCellText, type RuleRecord } from "./ruleBlocksData";
import { prepareClientAssignmentForm } from "./trafficAssignmentForm";
import { loadAllAssignments, loadAssignmentForm } from "./trafficAssignmentsApi";
import { assignmentError, type Assignment } from "./trafficAssignmentsData";

const Picker = styled(AssignmentSurface)`
	.target-context { margin: -10px 0 16px; color: #667085; font-size: 12px; }
	.target-context strong { color: #182230; font-weight: 600; }
	.rule-search { margin-bottom: 12px; }
	.rule-options { display: flex; flex-direction: column; gap: 8px; max-height: min(52dvh, 460px); margin: 0; padding: 0 2px 2px 0; overflow-y: auto; list-style: none; }
	.rule-option { display: block; width: 100%; padding: 12px 14px; border: 1px solid #d0d5dd; border-radius: 10px; background: #fff; color: inherit; font: inherit; text-align: left; cursor: pointer; }
	.rule-option:hover:not(:disabled) { border-color: #84adff; background: #f5f8ff; }
	.rule-option:focus-visible { outline: 2px solid #84adff; outline-offset: 1px; }
	.rule-option:disabled { background: #f9fafb; cursor: default; }
	.rule-option-head { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 8px; margin-bottom: 4px; }
	.rule-option-head strong { color: #182230; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
	.rule-option:disabled .rule-option-head strong { color: #667085; }
	.rule-badge { padding: 1px 8px; border-radius: 6px; background: #f2f4f7; color: #475467; font-size: 11px; white-space: nowrap; }
	.rule-badge.prevent { background: #fff1f3; color: #b42342; }
	.rule-badge.allow { background: #ecfdf3; color: #027a48; }
	.rule-badge.warn { background: #fffaeb; color: #93370d; }
	.rule-badge.assigned { background: #eff4ff; color: #175cd3; }
	.rule-option .rule-condition strong, .rule-option .rule-condition small { display: block; overflow-wrap: anywhere; }
	.rule-option .rule-condition strong { color: #344054; font-size: 13px; font-weight: 500; }
	.rule-option .rule-condition small { color: #667085; font-size: 12px; }
	.picker-state { padding: 28px 0; color: #667085; text-align: center; }
	.picker-footer { display: flex; justify-content: flex-end; margin-top: 16px; }
	.other-rule { display: inline-flex; align-items: center; gap: 4px; margin-left: auto; padding: 0; border: 0; background: none; color: #175cd3; font: inherit; font-size: 12px; cursor: pointer; }
	.other-rule:disabled { color: #98a2b3; cursor: default; }
`;

/** Every rule of the project, so any of them can be assigned. */
const loadAllRules = async (projectId: number, signal: AbortSignal) => {
	const rows: RuleRecord[] = [];
	for (let page = 0; page < 100; page++) {
		const response = await GD.REQ_API.request({ ...getBanRulesRequest(projectId, page, 100), signal });
		signal.throwIfAborted();
		if (response.error) throw response.error;
		const result = parseBanRules(response.data);
		if (!result) throw new Error("invalidResponse");
		rows.push(...result.rows);
		if (!result.rows.length || (result.totalPages !== null ? page + 1 >= result.totalPages : result.total !== null ? rows.length >= result.total : result.rows.length < 100)) break;
	}
	return rows;
};

type Step = { kind: "pick" } | { kind: "form"; rule: RuleRecord; model: RuleRecord };

/**
 * Assigns a rule to one client, or edits that assignment. The target is fixed, so the dialog starts by choosing a
 * rule and then shows the server's assignment form with the same thresholds as the rule popup.
 */
export const ClientRuleAssignDialog = ({ clientId, clientName, projectId, userId, edit, onClose, onSaved }: { clientId: number; clientName?: string; projectId: number; userId: number; edit?: { rule: RuleRecord; assignment: Assignment }; onClose: () => void; onSaved: () => void }) => {
	useLocale();
	const { t } = useTranslation();
	const label = (key: string) => t(`trafficAssignments.${key}`);
	const message = (value: string) => t(`trafficAssignments.${value}`, { defaultValue: value });
	const [step, setStep] = useState<Step | null>(edit ? null : { kind: "pick" });
	const [catalog, setCatalog] = useState<{ rules?: RuleRecord[]; assigned?: Set<number>; error?: string }>({});
	const [search, setSearch] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const dialogRef = useRef<HTMLDivElement>(null);
	const pending = useRef<AbortController | null>(null);
	const alive = useRef(true);
	const run = async (operation: (signal: AbortSignal) => Promise<void>) => {
		if (pending.current || !alive.current) return;
		const controller = new AbortController(); pending.current = controller; setBusy(true); setError("");
		try { await operation(controller.signal); }
		catch (cause) { if (alive.current && !controller.signal.aborted) setError(assignmentError(cause)); }
		finally { pending.current = null; if (alive.current) setBusy(false); }
	};
	const open = (rule: RuleRecord, key: Parameters<typeof loadAssignmentForm>[1]) => void run(async signal => {
		const model = await loadAssignmentForm(projectId, key, signal);
		if (alive.current && !signal.aborted) setStep({ kind: "form", rule, model: prepareClientAssignmentForm(model, clientId) });
	});
	useEffect(() => {
		alive.current = true;
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => { alive.current = false; pending.current?.abort(); onClose(); });
		const previous = document.activeElement;
		dialogRef.current?.focus({ preventScroll: true });
		if (edit) open(edit.rule, { rule_id: edit.assignment.rule_id, target_type: "client", target_id: clientId });
		return () => {
			alive.current = false; pending.current?.abort(); GD.S_AUTH_LOG_OUT.unsubscribe(logout);
			if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
		};
	}, []);
	useEffect(() => {
		if (edit) return;
		const controller = new AbortController();
		void Promise.all([loadAllRules(projectId, controller.signal), loadAllAssignments(projectId, { target_type: "client", target_id: clientId }, controller.signal)]).then(([rules, assignments]) => {
			if (!controller.signal.aborted) setCatalog({ rules, assigned: new Set(assignments.rows.map(row => row.rule_id)) });
		}).catch(cause => { if (!controller.signal.aborted) setCatalog({ error: assignmentError(cause) }); });
		return () => controller.abort();
	}, [projectId, clientId]);

	const close = () => { if (!pending.current) onClose(); };
	const submit = async (model: RuleRecord) => {
		if (step?.kind !== "form" || pending.current || !alive.current) throw new Error(t("bans.saving"));
		const ruleId = banRuleId(step.rule);
		if (ruleId === null) throw new Error(message("invalidResponse"));
		const controller = new AbortController(); pending.current = controller; setBusy(true); setError("");
		try {
			// The form hides the target; never let a stale value move the assignment to another entity.
			const result = await saveAssignmentForm({ model: { ...model, target_type: "client", target_id: clientId }, ruleId, existing: edit?.assignment, projectId, userId, label, signal: controller.signal });
			if ("error" in result) return result;
			if (!alive.current || controller.signal.aborted) throw new Error(label("contextChanged"));
			onSaved();
			return { data: model };
		} catch (cause) {
			if (cause && typeof cause === "object" && "code" in cause) return { error: cause };
			throw new Error(message(assignmentError(cause)));
		} finally { pending.current = null; if (alive.current) setBusy(false); }
	};

	const terms = search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
	const rules = (catalog.rules ?? []).filter(rule => terms.every(term => `${ruleCellText(rule.name)} ${rule.id}`.toLocaleLowerCase().includes(term)));
	const target = <p className="target-context">{label("clientAssign.client")} <strong>{clientName || `#${clientId}`}</strong> · ID {clientId}</p>;
	const title = label(edit ? "edit" : "add");
	const content = !step ? <div className="picker-state" role="status">{error ? message(error) : t("common.loading")}</div>
		: step.kind === "pick" ? <>
			<h4>{title}</h4>
			{target}
			<div className="rule-search"><AdminInput type="search" aria-label={label("clientAssign.search")} placeholder={label("clientAssign.search")} value={search} disabled={!catalog.rules} onChange={event => setSearch(event.target.value)} /></div>
			{catalog.error ? <div className="picker-state" role="alert">{message(catalog.error)}</div>
				: !catalog.rules ? <div className="picker-state" role="status">{t("common.loading")}</div>
				: !catalog.rules.length ? <div className="picker-state">{label("clientAssign.empty")}</div>
				: !rules.length ? <div className="picker-state">{label("clientAssign.noMatches")}</div>
				: <ul className="rule-options" aria-label={label("clientAssign.choose")}>{rules.map(rule => {
					const id = banRuleId(rule), assigned = id !== null && !!catalog.assigned?.has(id);
					const action = typeof rule.action === "string" ? rule.action : "";
					return <li key={String(rule.id)}><button type="button" className="rule-option" disabled={busy || assigned || id === null} onClick={() => id !== null && open(rule, { rule_id: id })}>
						<span className="rule-option-head">
							<strong>{ruleCellText(rule.name)}</strong>
							{action && <span className={`rule-badge ${action}`}>{t(`banRules.editor.options.${action}`, { defaultValue: action })}</span>}
							{rule.enabled === false && <span className="rule-badge">{label("ruleOff")}</span>}
							{assigned && <span className="rule-badge assigned">{label("alreadyAssigned")}</span>}
						</span>
						<TrafficRuleSummary row={rule} compact />
					</button></li>;
				})}</ul>}
			<div className="picker-footer"><AdminButton variant="secondarySmall" type="button" disabled={busy} onClick={close}>{t("bans.cancel")}</AdminButton></div>
		</>
		: <div className="assign-form"><fieldset disabled={busy} aria-busy={busy}>
			<h4>{title}</h4>
			{target}
			<div className="rule-context">
				<div className="rule-context-head"><span>{label("ruleContext")}</span><strong>{ruleCellText(step.rule.name)}</strong><span className="rule-context-count">{t("trafficAssignments.ruleConditionsCount", { count: readRulePreview(step.rule).conditions.length })}</span>
					{!edit && <button type="button" className="other-rule" disabled={busy} onClick={() => { setError(""); setStep({ kind: "pick" }); }}><ArrowLeft size={13} aria-hidden="true" />{label("clientAssign.other")}</button>}
				</div>
				<TrafficRuleSummary row={step.rule} numbered />
			</div>
			<FormParser<RuleRecord> grow size="small" ignoreObserver
				onRequest={async () => ({ data: step.model })}
				onSubmit={submit}
				primaryButton={{ title: busy ? t("bans.saving") : label("save") }}
				secondaryButton={{ title: t("bans.cancel"), onClick: close }} />
		</fieldset></div>;

	return createPortal(<AssignmentOverlay onMouseDown={event => { if (event.target === event.currentTarget) close(); }}><div ref={dialogRef} className="assignment-dialog" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} onKeyDown={event => {
		// Keep Escape and Tab inside this dialog; the client settings panel underneath listens on the document.
		event.stopPropagation();
		if (event.key === "Escape") { event.preventDefault(); close(); }
		if (event.key !== "Tab") return;
		const controls = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') ?? []).filter(element => element.getClientRects().length > 0);
		const first = controls[0], last = controls[controls.length - 1];
		if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last?.focus(); }
		else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
	}}><Picker className="form-surface">{error && step && <p className="error" role="alert">{message(error)}</p>}{content}</Picker></div></AssignmentOverlay>, document.body);
};
